// Builds the wrapped sim object used in a multiplayer match.
//
// LOCKSTEP RULE: a multiplayer client never mutates its sim directly. Every
// action is queued to the match-server as an intent; the server bundles all
// seats' intents into an `input:frame` for a tick, and EVERY client — the
// sender included — applies that frame at that tick. Applying your own action
// early would put your sim a tick ahead of everyone else's and break the
// determinism hash. So each method here only relays, and answers "submitted"
// (true / null) optimistically; the outcome shows up in the next snapshot.
//
// Read-only members (snapshot, events, theme, …) pass through from `mpSim`.

import type { HeroQuestKind } from '../../heroes/quests.js';
import type { AgentChoice, CaptureChoice } from '../../sim/useSimWorld/applyIntent.js';
import type { TreatyKind } from '../../sim/useSimWorld/useTreatyIntents.js';
import type { useMpSimWorld } from './useMpSimWorld.js';

export type WrappedSim = ReturnType<typeof buildWrappedSim>;

export function buildWrappedSim(
  mpSim: ReturnType<typeof useMpSimWorld>,
  relayIntent: (intent: Record<string, unknown>) => void,
  /** This client's seat. The server re-stamps it; sent for clarity and tests. */
  seat = 0,
) {
  const send = (kind: string, fields: Record<string, unknown> = {}): true => {
    relayIntent({ kind, playerIndex: seat, ...fields });
    return true;
  };
  return {
    ...mpSim,
    attackCity: (targetCityId: string) => void send('attack_city', { targetCityId }),
    moveUnit: (unitId: string, targetQ: number, targetR: number) =>
      send('move_unit', { unitId, targetQ, targetR }),
    sailShip: (unitId: string, targetQ: number, targetR: number) =>
      send('sail_ship', { unitId, targetQ, targetR }),
    buildBridge: (unitId: string, targetQ: number, targetR: number) =>
      send('build_bridge', { unitId, targetQ, targetR }),
    deployUnit: (unitId: string, targetQ: number, targetR: number) =>
      send('deploy_unit', { unitId, targetQ, targetR }),
    dockTrade: (unitId: string, cityId: string, food?: string) =>
      send('dock_trade', { unitId, cityId, food: food ?? 'grain' }),
    acceptMerchant: (cityId: string) => send('accept_merchant', { cityId }),
    declineMerchant: (cityId: string) => send('decline_merchant', { cityId }),
    startBuilding: (cityId: string, buildingId: string) =>
      send('start_building', { cityId, buildingId }),
    setCityDoctrine: (cityId: string, archetype: string) =>
      send('set_city_doctrine', { cityId, archetype }),
    invest: (archetype: Parameters<typeof mpSim.invest>[0]) =>
      void send('invest_doctrine', { archetype }),
    setFormation: (unitId: string, formation: string) =>
      void send('set_formation', { unitId, formation }),
    startResearch: () => void send('start_research'),
    pilgrimage: () => void send('pilgrimage'),
    reform: () => void send('reform'),
    // Champions and heroes. A null result means "accepted" in the SimWorld contract.
    summonLegend: (cityId: string): string | null => {
      send('summon_legend', { cityId });
      return null;
    },
    activateLegendAbility: (
      unitId: string,
      slot: number,
      targetQ: number,
      targetR: number,
    ): string | null => {
      send('activate_legend_ability', { unitId, slot, targetQ, targetR });
      return null;
    },
    activateHeroAbility: (unitId: string, slotIndex: number) =>
      void send('activate_hero_ability', { unitId, slotIndex }),
    selectHeroAbility: (unitId: string, slotIndex: number, abilityId: string) =>
      void send('select_hero_ability', { unitId, slotIndex, abilityId }),
    treaty: {
      propose: (toPlayer: number, treaty: TreatyKind) =>
        send('propose_treaty', { toPlayer, treaty }),
      accept: (fromPlayer: number) => send('accept_treaty', { fromPlayer }),
      decline: (fromPlayer: number) => send('decline_treaty', { fromPlayer }),
      declareWar: (toPlayer: number) => send('declare_war', { toPlayer }),
    },
    espionage: {
      dispatchSpy: (unitId: string, targetCityId: string) =>
        send('dispatch_spy', { unitId, targetCityId }),
      sabotageCity: (
        unitId: string,
        targetCityId: string,
        opts?: { buildingId?: string; frameSeat?: number },
      ) => send('sabotage_city', { unitId, targetCityId, ...opts }),
      assassinate: (unitId: string, royalId: number, frameSeat?: number) =>
        send('assassinate', { unitId, royalId, frameSeat }),
      poisonSupply: (
        unitId: string,
        targetCityId: string,
        supply: 'well' | 'granary',
        frameSeat?: number,
      ) => send('poison_supply', { unitId, targetCityId, supply, frameSeat }),
      postCounterSpy: (unitId: string, cityId: string) =>
        send('post_counter_spy', { unitId, cityId }),
      recallCounterSpy: (unitId: string) => send('recall_counter_spy', { unitId }),
      sweep: (unitId: string) => send('sweep', { unitId }),
      intercept: (unitId: string, targetUnitId: string) =>
        send('intercept', { unitId, targetUnitId }),
      decideAgent: (captiveId: number, choice: AgentChoice) =>
        send('decide_agent', { captiveId, choice }),
      chooseCapture: (royalId: number, choice: CaptureChoice) =>
        send('choose_capture', { royalId, choice }),
      spyRescue: (unitId: string, royalId: number) => send('spy_rescue', { unitId, royalId }),
      raidRescue: (royalId: number) => send('raid_rescue', { royalId }),
      payRansom: (royalId: number) => send('pay_ransom', { royalId }),
    },
    heroCaptivity: {
      chooseHeroCaptivity: (unitId: string, choice: CaptureChoice) =>
        send('choose_hero_captivity', { unitId, choice }),
      payHeroRansom: (unitId: string) => send('pay_hero_ransom', { unitId }),
    },
    heroQuests: {
      dispatchHeroQuest: (
        unitId: string,
        kind: HeroQuestKind,
        amount?: number,
        targetUnitId?: string,
      ) =>
        send('dispatch_hero_quest', {
          unitId,
          questKind: kind,
          ...(amount === undefined ? {} : { amount }),
          ...(targetUnitId === undefined ? {} : { targetId: targetUnitId }),
        }),
    },
    dynasty: {
      propose: (toPlayer: number) => send('propose_marriage', { toPlayer }),
      accept: (fromPlayer: number) => send('accept_marriage', { fromPlayer }),
      decline: (fromPlayer: number) => send('decline_marriage', { fromPlayer }),
    },
  };
}
